'use client'

import type { PointerEvent } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { faStar } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { styled } from 'styled-components'

import { FormattedMessage, Tooltip } from '@b-stock/fe-core/bstock-react'
import { Colors, Typography } from '@b-stock/fe-core/design-system'

const navy = Colors.Semantic.BStock.default.lightBackground

const Pill = styled.button`
  ${Typography.Body3_Semibold}
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  flex-shrink: 0;
  padding: 0.3125rem 0.75rem;
  border: 0.0625rem solid ${navy};
  border-radius: 999px;
  background: ${navy};
  color: ${Colors.Semantic.Neutral.white};
  letter-spacing: 0.03em;
  text-transform: uppercase;
  white-space: nowrap;
  cursor: pointer;
`

const isMouse = (event: PointerEvent) => event.pointerType === 'mouse'

/**
 * Closes a pinned explanation on a pointerdown anywhere outside the pill. iOS Safari does not
 * blur a tapped button when the next tap lands on the page, so blur alone would leave it open.
 */
const useCloseOnOutsidePointerDown = (
  pillElement: HTMLElement | null,
  isPinnedOpen: boolean,
  close: () => void
) => {
  useEffect(() => {
    if (!isPinnedOpen) return undefined
    const onPointerDown = (event: Event) => {
      if (!pillElement?.contains(event.target as Node)) close()
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [pillElement, isPinnedOpen, close])
}

/**
 * The Key Buyer Access pill in the storefront header. The explanation shows while a mouse hovers
 * (desktop) and toggles on tap, Enter or Space (Mobile web and keyboard). Hover is read from mouse
 * pointers only, so a tap's emulated hover cannot keep the explanation open after a second tap,
 * and a mouse click never pins it open: on desktop it follows the hover alone.
 */
export default function KeyBuyerAccessBadge() {
  const [pillElement, setPillElement] = useState<HTMLButtonElement | null>(null)
  const [isHovered, setIsHovered] = useState(false)
  const [isPinnedOpen, setIsPinnedOpen] = useState(false)
  // The pointer that started the current click; null for Enter or Space.
  const clickPointerType = useRef<string | null>(null)
  const close = useCallback(() => setIsPinnedOpen(false), [])

  useCloseOnOutsidePointerDown(pillElement, isPinnedOpen, close)

  const onClick = () => {
    const fromMouse = clickPointerType.current === 'mouse'
    clickPointerType.current = null
    if (!fromMouse) setIsPinnedOpen((open) => !open)
  }

  return (
    <>
      <Pill
        type="button"
        ref={setPillElement}
        data-testid="key-buyer-access-badge"
        aria-expanded={isPinnedOpen}
        onClick={onClick}
        onBlur={close}
        onPointerDown={(event) => {
          clickPointerType.current = event.pointerType
        }}
        onPointerEnter={(event) => isMouse(event) && setIsHovered(true)}
        onPointerLeave={(event) => isMouse(event) && setIsHovered(false)}
      >
        <FontAwesomeIcon icon={faStar} aria-hidden="true" />
        <FormattedMessage id="StorefrontAccess.keyBuyerBadge.label" />
      </Pill>
      <Tooltip
        referenceElement={pillElement}
        placement="bottom-start"
        isOpen={isHovered || isPinnedOpen}
      >
        <FormattedMessage id="StorefrontAccess.keyBuyerBadge.explanation" />
      </Tooltip>
    </>
  )
}
